import { z } from "zod";
import {
  ChartTypeSchema,
  SeriesTypeSchema,
  StrategySettingsSchema,
} from "./strategies";

const DateValueSchema = z.object({
  date: z.string(),
  value: z.number(),
}).strict();

const CandlestickDataSchema = z.object({
  date: z.string(),
  open: z.number(),
  high: z.number(),
  low: z.number(),
  close: z.number(),
}).strict();

const TrendLineSchema = z.object({
  barA: z.number().int(),
  barAExtension: z.number().int(),
  priceA: z.number(),
  barB: z.number().int(),
  barBExtension: z.number().int(),
  priceB: z.number(),
}).strict();

const CandlestickSeriesSchema = z.object({
  type: z.literal("Candlestick"),
  name: z.string(),
  data: z.array(CandlestickDataSchema),
  trendLines: z.array(TrendLineSchema),
}).strict();

const ValueSeriesSchema = z.object({
  type: z.enum(["Line", "Histogram"]),
  name: z.string(),
  data: z.array(DateValueSchema),
}).strict();

export const ChartModelSchema = z.object({
  version: z.number().int(),
  lastMarketCloseDate: z.string(),
  subCharts: z.array(z.object({
    dataSeries: z.array(z.discriminatedUnion("type", [
      CandlestickSeriesSchema,
      ValueSeriesSchema,
    ])),
    highlightFrom: z.number().int().nullable(),
    highlightTo: z.number().int().nullable(),
  }).strict()),
}).strict();

export const GenerateChartRequestSchema = z.object({
  ticker: z.string().trim().min(1),
  data: z.object({
    subStrategyId: z.string().nullable(),
    variablesCode: z.string(),
    criteriaCode: z.string(),
    trendLines: StrategySettingsSchema.shape.trendLines,
    chartBarCountExpr: z.string().nullable(),
    chartType: ChartTypeSchema,
    chartHighlightedBarCount: z.number().int(),
    customChartVariable: StrategySettingsSchema.shape.customChartVariable,
    overlaidChartVariables: z.array(z.object({
      name: z.string(),
      variableExpression: z.string(),
      includeTimestampExpression: z.string(),
      seriesType: SeriesTypeSchema,
    }).strict()),
    subChartVariables: StrategySettingsSchema.shape.subChartVariables,
    subStrategies: StrategySettingsSchema.shape.subStrategies,
  }).strict(),
}).strict();

export type ChartModel = z.infer<typeof ChartModelSchema>;
export type ChartSubChart = ChartModel["subCharts"][number];
export type ChartSeries = ChartSubChart["dataSeries"][number];
export type ChartCandlestickSeries = z.infer<typeof CandlestickSeriesSchema>;
export type ChartLineSeries = z.infer<typeof ValueSeriesSchema> & { type: "Line" };
export type ChartHistogramSeries = z.infer<typeof ValueSeriesSchema> & { type: "Histogram" };
export type GenerateChartRequest = z.infer<typeof GenerateChartRequestSchema>;
